
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Store } from '@/types';
import { getStoresFromDatabase } from '@/services/store';
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useStandardizedError, useStandardizedLoading } from "@/hooks/useStandardizedError";
import { AdminPagination, usePagination } from '@/components/ui/admin-pagination';
import { supabase } from "@/integrations/supabase/client";
import { invokeAdmin } from "@/services/functions";
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { checkStoreDependencies } from '@/utils/referentialIntegrity';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

const LocationsManager = () => {
  const { handleError, handleSuccess } = useStandardizedError();
  const { setLoading, isLoading } = useStandardizedLoading();
  const { pagination, setPage, setLimit, setTotal } = usePagination(10);
  const [locations, setLocations] = useState<Store[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    locationId: string;
    locationName: string;
  }>({
    open: false,
    locationId: '',
    locationName: ''
  });
  
  const [formData, setFormData] = useState<{
    name: string;
    location: string;
  }>({
    name: "",
    location: "",
  });

  useEffect(() => {
    fetchLocations();
  }, [pagination.page, pagination.limit]);

  const fetchLocations = async () => {
    setLoading('fetch', true);
    try {
      const { data, error } = await invokeAdmin('admin-manage-stores', {
        action: 'list',
        pagination: {
          page: pagination.page,
          limit: pagination.limit
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao caregar locais');
      }

      setLocations(data.stores || []);
      if (data.pagination) {
        setTotal(data.pagination.total || 0);
      }
    } catch (error: any) {
      handleError(error, 'LocationsManager.fetchLocations');
    } finally {
      setLoading('fetch', false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      location: "",
    });
    setIsAdding(false);
    setEditingId(null);
  };

  const handleAddLocation = async () => {
    if (!formData.name) {
      handleError(new Error("Nome do local é obrigatório"), 'LocationsManager.handleAddLocation');
      return;
    }

    try {
      const { data, error } = await invokeAdmin('admin-manage-stores', {
        action: 'create',
        storeData: {
          name: formData.name,
          address: formData.location || '',
          city: '',
          country: 'Portugal'
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao criar local');

      handleSuccess("Local criado com sucesso");

      resetForm();
      fetchLocations();
    } catch (error: any) {
      handleError(error, 'LocationsManager.handleAddLocation');
    }
  };

  const handleEditLocation = async () => {
    if (!editingId) return;

    try {
      const { data, error } = await invokeAdmin('admin-manage-stores', {
        action: 'update',
        storeId: editingId,
        storeData: {
          name: formData.name,
          address: formData.location || ''
        }
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao actualizar local');

      handleSuccess("Local actualizado com sucesso");

      resetForm();
      fetchLocations();
    } catch (error: any) {
      handleError(error, 'LocationsManager.handleEditLocation');
    }
  };

  const startEditing = (location: Store) => {
    setFormData({
      name: location.name,
      location: location.address || location.location || "",
    });
    setEditingId(location.id);
    setIsAdding(true);
  };

  const openDeleteConfirmation = async (location: Store) => {
    setLoading('checkDependencies', true);
    try {
      const dependencyCheck = await checkStoreDependencies(location.id);

      if (dependencyCheck.hasDependent) {
        handleError(new Error(dependencyCheck.message), 'LocationsManager.openDeleteConfirmation');
        return;
      }

      setDeleteConfirmation({
        open: true,
        locationId: location.id,
        locationName: location.name
      });
    } catch (error) {
      handleError(error, 'LocationsManager.openDeleteConfirmation');
    } finally {
      setLoading('checkDependencies', false);
    }
  };

  const handleDeleteLocation = async (id: string) => {
    try {
      const { data, error } = await invokeAdmin('admin-manage-stores', {
        action: 'delete',
        storeId: id
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao eliminar local');

      handleSuccess("Local eliminado com sucesso");

      fetchLocations();
    } catch (error: any) {
      handleError(error, 'LocationsManager.handleDeleteLocation');
    }
  };

  return (
    <div>
      {isAdding ? (
        <div className="bg-brand-dark p-4 rounded-md mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingId ? "Editar Local" : "Adicionar Novo Local"}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm mb-1">Nome*</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="bg-brand-darker text-white"
                placeholder="Nome do local"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Localização</label>
              <Input
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="bg-brand-darker text-white"
                placeholder="Cidade, País"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={editingId ? handleEditLocation : handleAddLocation}
              className="bg-brand-red text-white hover:bg-brand-red/90"
            >
              {editingId ? "Salvar Alterações" : "Adicionar Local"}
            </Button>
            <Button
              onClick={resetForm}
              variant="outline"
              className="bg-transparent border-gray-600 text-white hover:bg-gray-800"
            >
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <Button
          onClick={() => setIsAdding(true)}
          className="mb-6 bg-brand-red text-white hover:bg-brand-red/90"
        >
          <Plus size={16} className="mr-2" /> Adicionar Local
        </Button>
      )}

      <div className="rounded-md border border-gray-700 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-dark hover:bg-brand-dark/80">
              <TableHead className="text-white">Nome</TableHead>
              <TableHead className="text-white">Localização</TableHead>
              <TableHead className="text-white w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading('fetch') ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-6 text-gray-400">
                  A caregar...
                </TableCell>
              </TableRow>
            ) : locations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-6 text-gray-400">
                  Nenhum local registado
                </TableCell>
              </TableRow>
            ) : (
              locations.map(location => (
                <TableRow key={location.id} className="hover:bg-brand-dark/50">
                  <TableCell className="font-medium">{location.name}</TableCell>
                  <TableCell>{location.address || location.location || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEditing(location)}
                        className="h-8 w-8 text-gray-400 hover:text-white hover:bg-gray-800"
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openDeleteConfirmation(location)}
                        disabled={isLoading('checkDependencies')}
                        className="h-8 w-8 text-gray-400 hover:text-white hover:bg-gray-800"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {locations.length > 0 && (
        <div className="mt-4">
          <AdminPagination
            pagination={pagination}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </div>
      )}

      <ConfirmationDialog
        open={deleteConfirmation.open}
        onOpenChange={(open) => setDeleteConfirmation(prev => ({ ...prev, open }))}
        title="Confirmar eliminação"
        description={`Tem a certeza que pretende eliminar o local "${deleteConfirmation.locationName}"? Esta ação não pode ser desfeita.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={() => handleDeleteLocation(deleteConfirmation.locationId)}
        destructive={true}
      />
    </div>
  );
};

export default LocationsManager;
